import { useState } from 'react'
import { CheckCircle2, Loader2, XCircle, Zap } from 'lucide-react'
import { Bubble, BubbleContent } from '../ui/bubble'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { cn } from '@/lib/utils'
import { captureError } from '@/lib/sentry'
import { AgentActionStatusEnum, type PendingAction } from '@/gen/cortado/v1/agent_pb'
import { humanizeActionType } from './chat-utils'

interface ActionProposalCardProps {
  proposal: PendingAction
  onApprove: (proposalId: string) => Promise<void>
  onReject: (proposalId: string) => Promise<void>
}

export function ActionProposalCard({ proposal, onApprove, onReject }: ActionProposalCardProps) {
  const [pendingAction, setPendingAction] = useState<'approve' | 'reject' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const status = proposal.status
  const isProposed = status === AgentActionStatusEnum.AGENT_ACTION_STATUS_PROPOSED
  const isExecuting =
    status === AgentActionStatusEnum.AGENT_ACTION_STATUS_APPROVED ||
    status === AgentActionStatusEnum.AGENT_ACTION_STATUS_EXECUTING
  const isExecuted = status === AgentActionStatusEnum.AGENT_ACTION_STATUS_EXECUTED
  const isRejected = status === AgentActionStatusEnum.AGENT_ACTION_STATUS_REJECTED
  const isFailed = status === AgentActionStatusEnum.AGENT_ACTION_STATUS_FAILED

  const variant = isFailed ? 'destructive' : isExecuted ? 'tinted' : isRejected ? 'muted' : 'outline'
  const title = proposal.preview?.title || humanizeActionType(proposal.actionType)
  const fields = proposal.preview?.fields ?? []

  const handleApprove = async () => {
    if (pendingAction) return
    setPendingAction('approve')
    setError(null)
    try {
      await onApprove(proposal.id)
    } catch (err) {
      captureError(err instanceof Error ? err : new Error(String(err)), { context: 'action-proposal-approve' })
      setError(err instanceof Error ? err.message : 'Failed to approve action')
    } finally {
      setPendingAction(null)
    }
  }

  const handleReject = async () => {
    if (pendingAction) return
    setPendingAction('reject')
    setError(null)
    try {
      await onReject(proposal.id)
    } catch (err) {
      captureError(err instanceof Error ? err : new Error(String(err)), { context: 'action-proposal-reject' })
      setError(err instanceof Error ? err.message : 'Failed to reject action')
    } finally {
      setPendingAction(null)
    }
  }

  return (
    <Bubble variant={variant} className="w-full max-w-full">
      <BubbleContent className="w-full space-y-2.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium">{title}</span>
          {proposal.executedByAutopilot && (
            <Badge variant="secondary" className="shrink-0">
              <Zap className="h-3 w-3" />
              Autopilot
            </Badge>
          )}
        </div>

        {proposal.description && <p className="text-sm text-muted-foreground">{proposal.description}</p>}

        {fields.length > 0 && (
          <div className="space-y-1 rounded-lg border bg-background/50 p-2.5">
            {fields.map((field) => (
              <div key={field.label} className="flex items-center justify-between gap-3 text-xs">
                <span className="text-muted-foreground">{field.label}</span>
                <span className="font-medium text-right">{field.value}</span>
              </div>
            ))}
          </div>
        )}

        {proposal.preview?.body && <p className="text-xs text-muted-foreground">{proposal.preview.body}</p>}

        {(error || (isFailed && proposal.error)) && (
          <p className="text-xs text-destructive">{error || proposal.error}</p>
        )}

        {isProposed && (
          <div className="flex gap-2 pt-0.5">
            <Button size="sm" onClick={handleApprove} disabled={pendingAction !== null} className="flex-1">
              {pendingAction === 'approve' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              Approve
            </Button>
            <Button size="sm" variant="outline" onClick={handleReject} disabled={pendingAction !== null} className="flex-1">
              {pendingAction === 'reject' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5" />}
              Reject
            </Button>
          </div>
        )}

        {isExecuting && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span>Executing…</span>
          </div>
        )}

        {isExecuted && (
          <div className={cn('flex items-center gap-1.5 text-xs text-green-700 dark:text-green-400')}>
            <CheckCircle2 className="h-3 w-3" />
            <span>{proposal.executedByAutopilot ? 'Executed automatically' : 'Executed'}</span>
          </div>
        )}

        {isRejected && <div className="text-xs text-muted-foreground">Rejected</div>}
      </BubbleContent>
    </Bubble>
  )
}
